/** Account settings: display name, target level, daily goal, avatar and password. */

import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { ApiError, api, type Level } from '../api'
import { Avatar, ErrorNote, LevelPicker, Spinner } from '../components/ui'
import { useAuth } from '../store/auth'

/** Preset seeds only change the avatar's colour, so the labels are just flavour. */
const SEEDS = ['sakura', 'matcha', 'kitsune', 'sumi', 'kohaku', 'indigo', 'hinoki']

const MIN_PASSWORD = 8

function errText(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Could not reach the server.'
}

function memberSince(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

export default function Profile() {
  const { user, loading, signOut, setUser } = useAuth()

  const [name, setName] = useState(user?.display_name ?? '')
  const [level, setLevel] = useState<Level>(user?.target_level ?? 'N5')
  const [goal, setGoal] = useState(String(user?.daily_goal ?? 10))
  // Older accounts may carry a seed that is not one of the presets; fall back so a
  // swatch is always shown as selected rather than none of them.
  const [seed, setSeed] = useState(
    user && SEEDS.includes(user.avatar_seed) ? user.avatar_seed : SEEDS[0],
  )
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [changing, setChanging] = useState(false)
  const [changed, setChanged] = useState(false)
  const [pwError, setPwError] = useState<string | null>(null)

  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // The server is the source of truth: re-seed the form whenever the stored user
  // changes, which is also how a successful save clears the "dirty" state.
  useEffect(() => {
    if (!user) return
    setName(user.display_name)
    setLevel(user.target_level)
    setGoal(String(user.daily_goal))
    setSeed(user.avatar_seed)
  }, [user])

  async function saveProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const trimmed = name.trim()
    const goalNumber = Number(goal)
    setSaved(false)
    if (trimmed.length === 0) {
      setError('Display name cannot be empty.')
      return
    }
    if (!Number.isInteger(goalNumber) || goalNumber < 1 || goalNumber > 200) {
      setError('Daily goal must be a whole number between 1 and 200.')
      return
    }
    setError(null)
    setSaving(true)
    try {
      const updated = await api.updateMe({
        display_name: trimmed,
        target_level: level,
        daily_goal: goalNumber,
        avatar_seed: seed,
      })
      setUser(updated)
      if (mounted.current) setSaved(true)
    } catch (err) {
      if (mounted.current) setError(errText(err))
    } finally {
      if (mounted.current) setSaving(false)
    }
  }

  async function changePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setChanged(false)
    if (newPassword.length < MIN_PASSWORD) {
      setPwError(`The new password needs at least ${MIN_PASSWORD} characters.`)
      return
    }
    if (newPassword !== confirmPassword) {
      setPwError('The two new passwords do not match.')
      return
    }
    setPwError(null)
    setChanging(true)
    try {
      await api.changePassword({ current_password: currentPassword, new_password: newPassword })
      if (!mounted.current) return
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setChanged(true)
    } catch (err) {
      if (mounted.current) setPwError(errText(err))
    } finally {
      if (mounted.current) setChanging(false)
    }
  }

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-2xl px-5 py-8">
        <Spinner label="Loading your profile…" />
      </div>
    )
  }
  if (!user) return <Navigate to="/login" replace />

  const dirty =
    name.trim() !== user.display_name ||
    level !== user.target_level ||
    goal !== String(user.daily_goal) ||
    seed !== user.avatar_seed

  return (
    <div className="mx-auto w-full max-w-2xl space-y-5 px-5 py-8">
      <header className="flex items-center gap-4">
        <Avatar name={name || user.display_name} seed={seed} size={56} />
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold text-slate-100">{user.display_name}</h1>
          <p className="truncate text-sm text-ink-500">{user.email}</p>
          <p className="text-xs text-ink-500">Member since {memberSince(user.created_at)}</p>
        </div>
      </header>

      <form className="card space-y-6 p-6" onSubmit={saveProfile}>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Profile</h2>

        <div>
          <label className="label" htmlFor="profile-name">
            Display name
          </label>
          <input
            id="profile-name"
            className="input"
            type="text"
            maxLength={40}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <p className="mt-1.5 text-xs text-ink-500">Shown on the scoreboard.</p>
        </div>

        <fieldset>
          <legend className="label">Target level</legend>
          <LevelPicker value={level} onChange={setLevel} />
        </fieldset>

        <div>
          <label className="label" htmlFor="profile-goal">
            Daily goal
          </label>
          <input
            id="profile-goal"
            className="input max-w-[8rem]"
            type="number"
            min={1}
            max={200}
            step={1}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
          />
          <p className="mt-1.5 text-xs text-ink-500">Cards to review per day, between 1 and 200.</p>
        </div>

        <fieldset>
          <legend className="label">Avatar</legend>
          <div className="flex flex-wrap gap-2">
            {SEEDS.map((option) => {
              const active = seed === option
              return (
                <button
                  key={option}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setSeed(option)}
                  className={`btn flex-col gap-1 border px-3 py-2 ${
                    active ? 'border-brand-500/60 bg-brand-500/10' : 'border-ink-700 bg-ink-850 hover:border-ink-600'
                  }`}
                >
                  <Avatar name={name || user.display_name} seed={option} size={32} />
                  <span className="text-[10px] font-medium text-ink-500">{option}</span>
                </button>
              )
            })}
          </div>
        </fieldset>

        {error && <ErrorNote>{error}</ErrorNote>}

        <div className="flex items-center gap-3">
          <button type="submit" className="btn-primary" disabled={saving || !dirty}>
            Save changes
          </button>
          {saving && <Spinner label="Saving…" />}
          {saved && !saving && <span className="text-xs font-semibold text-brand-500">Saved.</span>}
        </div>
      </form>

      <form className="card space-y-5 p-6" onSubmit={changePassword}>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Password</h2>

        <div>
          <label className="label" htmlFor="profile-current-password">
            Current password
          </label>
          <input
            id="profile-current-password"
            className="input"
            type="password"
            required
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="profile-new-password">
              New password
            </label>
            <input
              id="profile-new-password"
              className="input"
              type="password"
              required
              minLength={MIN_PASSWORD}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="profile-confirm-password">
              Repeat new password
            </label>
            <input
              id="profile-confirm-password"
              className="input"
              type="password"
              required
              minLength={MIN_PASSWORD}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
        </div>

        {pwError && <ErrorNote>{pwError}</ErrorNote>}

        <div className="flex items-center gap-3">
          <button type="submit" className="btn-ghost" disabled={changing}>
            Change password
          </button>
          {changing && <Spinner label="Updating…" />}
          {changed && !changing && <span className="text-xs font-semibold text-brand-500">Password changed.</span>}
        </div>
      </form>

      <div className="card flex flex-wrap items-center justify-between gap-3 p-6">
        <div>
          <h2 className="text-sm font-semibold text-slate-100">Sign out</h2>
          <p className="text-xs text-ink-500">Your progress stays on the server.</p>
        </div>
        <button type="button" className="btn-danger" onClick={signOut}>
          Sign out
        </button>
      </div>
    </div>
  )
}
